import React, { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import AuthLayout from "@/layouts/AuthLayout";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import PasswordInput from "@/components/PasswordInput";
import api from "@/lib/api";
import { formatApiError } from "@/lib/api";
import { toast } from "sonner";

// Lightweight RFC-5322-ish email matcher. Good enough to catch typos
// client-side; the EmailStr Pydantic validator on the backend is the
// canonical source of truth for what's actually accepted.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

export default function Register() {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [emailTouched, setEmailTouched] = useState(false);
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [confirmTouched, setConfirmTouched] = useState(false);
  const [acceptAll, setAcceptAll] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  // ----- Derived validity flags -----
  const emailValid = useMemo(() => EMAIL_RE.test(email.trim()), [email]);
  const emailError = emailTouched && email.length > 0 && !emailValid
    ? "Inserisci un'email valida (es. nome@dominio.it)."
    : "";

  const passwordsMatch = password.length > 0 && password === passwordConfirm;
  const passwordMatchError = confirmTouched && passwordConfirm.length > 0 && !passwordsMatch
    ? "Le password non coincidono."
    : "";

  const canSubmit =
    name.trim().length > 0 &&
    emailValid &&
    password.length >= 6 &&
    passwordsMatch &&
    acceptAll &&
    !busy;

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!emailValid) { setError("Inserisci un'email valida."); return; }
    if (password.length < 6) { setError("La password deve contenere almeno 6 caratteri."); return; }
    if (!passwordsMatch) { setError("Le password non coincidono."); return; }
    if (!acceptAll) { setError("Devi accettare Privacy Policy e Termini di Servizio per continuare."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/register", {
        name: name.trim(),
        email: email.trim().toLowerCase(),
        password,
        password_confirm: passwordConfirm,
        accepted_privacy: true,
        accepted_terms: true,
      });
      if (data?.requires_verification) {
        toast.success("Codice inviato. Controlla la tua casella di posta.");
        navigate(`/verify-email?email=${encodeURIComponent(data.email || email.trim().toLowerCase())}`, { replace: true });
        return;
      }
      // Defensive fallback — shouldn't happen with the new flow, but keeps
      // the UI moving if the backend ever short-circuits verification.
      toast.success("Account creato.");
      navigate("/login", { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout title="Crea il tuo account" subtitle="Inizia gratis — nessuna carta">
      <form onSubmit={onSubmit} className="space-y-5" data-testid="register-form" noValidate>
        <div>
          <Label htmlFor="name" className="label-uppercase">Nome e cognome</Label>
          <Input
            id="name"
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="mt-2 rounded-sm h-11"
            data-testid="register-name-input"
          />
        </div>

        <div>
          <Label htmlFor="email" className="label-uppercase">Email</Label>
          <Input
            id="email"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onBlur={() => setEmailTouched(true)}
            className={`mt-2 rounded-sm h-11 ${emailError ? "border-destructive focus-visible:ring-destructive/40" : ""}`}
            data-testid="register-email-input"
            aria-invalid={!!emailError}
          />
          {emailError && (
            <p className="text-xs text-destructive mt-1.5" data-testid="register-email-error">{emailError}</p>
          )}
        </div>

        <div>
          <Label htmlFor="password" className="label-uppercase">Password (min 6 caratteri)</Label>
          <PasswordInput
            id="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="mt-2 rounded-sm h-11"
            testid="register-password-input"
            toggleTestid="register-password-toggle"
            autoComplete="new-password"
          />
        </div>

        <div>
          <Label htmlFor="password-confirm" className="label-uppercase">Conferma password</Label>
          <PasswordInput
            id="password-confirm"
            required
            value={passwordConfirm}
            onChange={(e) => setPasswordConfirm(e.target.value)}
            onBlur={() => setConfirmTouched(true)}
            className={`mt-2 rounded-sm h-11 ${passwordMatchError ? "border-destructive focus-visible:ring-destructive/40" : ""}`}
            testid="register-password-confirm-input"
            toggleTestid="register-password-confirm-toggle"
            autoComplete="new-password"
            aria-invalid={!!passwordMatchError}
          />
          {passwordMatchError && (
            <p className="text-xs text-destructive mt-1.5" data-testid="register-password-mismatch-error">
              {passwordMatchError}
            </p>
          )}
          {confirmTouched && passwordsMatch && (
            <p className="text-xs text-emerald-600 mt-1.5" data-testid="register-password-match-ok">
              Le password coincidono.
            </p>
          )}
        </div>

        <label className={`flex items-start gap-3 p-3 rounded-sm border cursor-pointer transition-colors ${
          acceptAll ? "border-primary bg-primary/5" : "border-border hover:border-primary/40"
        }`}>
          <Checkbox
            checked={acceptAll}
            onCheckedChange={(c) => setAcceptAll(!!c)}
            data-testid="register-accept-checkbox"
            className="mt-0.5"
          />
          <span className="text-sm leading-snug text-neutral-700">
            Ho letto e accetto la{" "}
            <Link to="/privacy" target="_blank" className="text-primary underline hover:no-underline" data-testid="register-privacy-link">Privacy Policy</Link>
            {" "}e i{" "}
            <Link to="/termini" target="_blank" className="text-primary underline hover:no-underline" data-testid="register-terms-link">Termini di Servizio</Link>.
          </span>
        </label>

        {error && (
          <div className="text-sm text-destructive border border-destructive/30 bg-destructive/5 p-3 rounded-sm" data-testid="register-error">
            {error}
          </div>
        )}

        <Button
          type="submit"
          disabled={!canSubmit}
          className="w-full h-11 rounded-sm"
          data-testid="register-submit-button"
        >
          {busy ? "Creazione account…" : "Crea account"}
        </Button>
      </form>

      <div className="mt-8 text-sm text-neutral-600">
        Hai già un account?{" "}
        <Link to="/login" className="text-primary hover:underline font-medium" data-testid="register-login-link">
          Accedi
        </Link>
      </div>
    </AuthLayout>
  );
}
